/**
 * VelocityMarqueePrimary Component
 *
 * A continuous scrolling marquee component that alternates between icons and text.
 * Uses Framer Motion for smooth animation and Next.js Image component for optimized image loading.
 */

"use client";

import React from "react";
import Image from "next/image";
import { StaticImageData } from "next/image";
import { motion } from "motion/react";

// Import static assets
import paperPlane from "../../assets/icons/paoer-plane.svg";
import eyes from "../../assets/icons/eyes.svg";
import sparkle from "../../assets/icons/sparkle.svg";

/**
 * Defines the structure for items in the marquee.
 * Each item can be either an icon (with image source and alt text) or text content.
 */
type MarqueeItem =
  | { type: "icon"; content: StaticImageData; alt: string }
  | { type: "text"; content: string };

// Configuration for marquee items
const marqueeItems: MarqueeItem[] = [
  { type: "icon", content: paperPlane, alt: "paper plane" },
  { type: "text", content: "Learn" },
  { type: "icon", content: eyes, alt: "eyes" },
  { type: "text", content: "Improve" },
  { type: "icon", content: sparkle, alt: "sparkle" },
  { type: "text", content: "Study" },
];

/**
 * MarqueeContent Component
 * Renders the individual items in the marquee, handling both icons and text.
 */
const MarqueeContent = () => {
  return marqueeItems.map((item, index) => {
    if (item.type === "icon") {
      return (
        <Image
          src={item.content}
          alt={item.alt}
          key={item.alt + index}
          className="max-sm:w-[30px]"
        />
      );
    }
    return (
      <span
        key={index}
        className="text-3xl sm:text-5xl font-normal tracking-tighter"
      >
        {item.content}
      </span>
    );
  });
};

interface ParallaxProps {
  children: React.ReactNode;
}

/**
 * ParallaxScroll Component
 * Creates an infinite scrolling effect using Framer Motion.
 * @param {ParallaxProps} props - Component props containing children elements
 */
const ParallaxScroll = ({ children }: ParallaxProps) => {
  return (
    <div className="relative flex overflow-hidden">
      <motion.div
        className="flex gap-x-6 whitespace-nowrap"
        animate={{
          x: ["-0%", "-50%"],
        }}
        transition={{
          duration: 20,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }}
      >
        {Array(10)
          .fill(null)
          .map((_, index) => (
            <React.Fragment key={index}>{children}</React.Fragment>
          ))}
      </motion.div>
    </div>
  );
};

/**
 * VelocityMarqueePrimary Component
 * Main component that combines ParallaxScroll and MarqueeContent
 * to create a primary-colored marquee section.
 */
const VelocityMarqueePrimary = () => {
  return (
    <div className="bg-primary w-full relative z-0 h-[100px] sm:h-[150px]">
      <div className="flex items-center absolute inset-0 whitespace-nowrap gap-x-4 sm:gap-x-6 overflow-hidden h-full">
        <ParallaxScroll>
          <MarqueeContent />
        </ParallaxScroll>
      </div>
    </div>
  );
};

export default VelocityMarqueePrimary;
